import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Plus, Trash2, Copy, Check, Webhook, Send, ShieldCheck, ShieldOff, Save } from 'lucide-react';

const API = '/api';

const CopyBtn = ({ value, testId }) => {
  const [ok, setOk] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={async () => { await navigator.clipboard.writeText(value); setOk(true); setTimeout(() => setOk(false), 1500); }}
      className="text-zinc-500 hover:text-white p-1 transition-colors"
      title="Copiar"
    >
      {ok ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const NewKeyReveal = ({ created, onClose }) => (
  <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4 space-y-3" data-testid="new-key-reveal">
    <p className="text-sm text-emerald-300 font-semibold">Key creada: {created.name}</p>
    <p className="text-xs text-zinc-400">Guarda estos valores ahora. La key completa <b>no se volvera a mostrar</b>.</p>
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">API Key (header X-API-Key)</p>
      <div className="flex items-center gap-2 bg-zinc-950 rounded-lg px-3 py-2">
        <code className="text-xs font-mono text-cyan-300 flex-1 break-all" data-testid="new-key-value">{created.key}</code>
        <CopyBtn value={created.key} testId="copy-new-key-btn" />
      </div>
    </div>
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Webhook secret (firma HMAC)</p>
      <div className="flex items-center gap-2 bg-zinc-950 rounded-lg px-3 py-2">
        <code className="text-xs font-mono text-amber-300 flex-1 break-all" data-testid="new-key-secret">{created.webhook_secret}</code>
        <CopyBtn value={created.webhook_secret} testId="copy-new-secret-btn" />
      </div>
    </div>
    <button onClick={onClose} data-testid="close-new-key-btn" className="text-xs text-zinc-400 hover:text-white underline underline-offset-2">Listo, ya la guarde</button>
  </div>
);

const KeyRow = ({ k, onChanged }) => {
  const [url, setUrl] = useState(k.webhook_url || '');
  const [testing, setTesting] = useState(false);

  const saveUrl = async () => {
    try {
      await axios.put(`${API}/api-keys/${k.id}`, { webhook_url: url });
      toast.success('Webhook actualizado');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };

  const testWebhook = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/api-keys/${k.id}/test-webhook`, { url });
      if (res.data.ok) toast.success(`Webhook OK (HTTP ${res.data.status_code}, ${res.data.duration_ms} ms)`);
      else toast.error(`Webhook fallo: ${res.data.error}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al probar webhook'); }
    finally { setTesting(false); }
  };

  const toggle = async () => {
    try {
      await axios.put(`${API}/api-keys/${k.id}`, { active: !k.active });
      toast.success(k.active ? 'Key revocada' : 'Key reactivada');
      onChanged();
    } catch (e) { toast.error('Error al actualizar'); }
  };

  return (
    <div className={`bg-zinc-900/60 border rounded-xl p-4 space-y-3 ${k.active ? 'border-white/5' : 'border-red-500/20 opacity-70'}`} data-testid={`api-key-row-${k.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white truncate" data-testid={`api-key-name-${k.id}`}>{k.name}</h3>
            {k.active
              ? <span className="text-[9px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5 font-mono">ACTIVA</span>
              : <span className="text-[9px] bg-red-500/15 text-red-400 border border-red-500/30 rounded px-1.5 py-0.5 font-mono">REVOCADA</span>}
          </div>
          <code className="text-xs font-mono text-cyan-500/80" data-testid={`api-key-masked-${k.id}`}>{k.key_masked}</code>
          <p className="text-[10px] text-zinc-500 mt-1 font-mono">
            Creada {k.created_at?.slice(0, 10)} · Usos: {k.usage_count || 0} · Ultimo uso: {k.last_used_at ? new Date(k.last_used_at).toLocaleString('es-ES') : 'nunca'}
          </p>
        </div>
        <button onClick={toggle} data-testid={`toggle-api-key-${k.id}`} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${k.active ? 'text-red-400 border-red-500/30 hover:bg-red-500/10' : 'text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10'}`}>
          {k.active ? <><Trash2 className="w-3.5 h-3.5" /> Revocar</> : <><ShieldCheck className="w-3.5 h-3.5" /> Reactivar</>}
        </button>
      </div>

      <div className="bg-zinc-950/60 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500"><Webhook className="w-3 h-3" /> Webhook (POST al cambiar el estado)</div>
        <div className="flex gap-2">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://tu-servidor.com/webhooks/rcs"
            data-testid={`webhook-url-input-${k.id}`}
            className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-1.5 text-xs font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/50"
          />
          <button onClick={saveUrl} data-testid={`save-webhook-btn-${k.id}`} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white"><Save className="w-3.5 h-3.5" /> Guardar</button>
          <button onClick={testWebhook} disabled={testing || !url} data-testid={`test-webhook-btn-${k.id}`} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border border-cyan-500/30 disabled:opacity-40"><Send className="w-3.5 h-3.5" /> {testing ? 'Enviando…' : 'Probar'}</button>
        </div>
        <div className="flex items-center justify-between text-[10px] font-mono">
          <span className="text-zinc-500 flex items-center gap-1">Secret: <code className="text-amber-400/80">{k.webhook_secret ? `${k.webhook_secret.slice(0, 10)}…${k.webhook_secret.slice(-4)}` : '—'}</code>{k.webhook_secret && <CopyBtn value={k.webhook_secret} testId={`copy-secret-${k.id}`} />}</span>
          {k.webhook_last_status && (
            <span className={k.webhook_last_status === 'delivered' ? 'text-emerald-400' : 'text-red-400'} data-testid={`webhook-last-status-${k.id}`}>
              Ultimo envio: {k.webhook_last_status === 'delivered' ? 'entregado' : `fallo (${k.webhook_last_error || 'error'})`} · {k.webhook_last_at ? new Date(k.webhook_last_at).toLocaleTimeString('es-ES') : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const ApiKeysPanel = ({ onKeyCreated }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', webhook_url: '' });
  const [created, setCreated] = useState(null);

  const load = async () => {
    try { setKeys((await axios.get(`${API}/api-keys`)).data); }
    catch (e) { toast.error('Error al cargar API keys'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const res = await axios.post(`${API}/api-keys`, form);
      setCreated(res.data.api_key);
      onKeyCreated && onKeyCreated(res.data.api_key.key);
      setShowCreate(false);
      setForm({ name: '', webhook_url: '' });
      toast.success('API key creada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear key'); }
  };

  return (
    <div className="space-y-4" data-testid="api-keys-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-zinc-300"><KeyRound className="w-4 h-4 text-cyan-400" /> {keys.filter((k) => k.active).length} key(s) activa(s)</div>
        <button onClick={() => setShowCreate(true)} data-testid="create-api-key-btn" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Nueva API Key</button>
      </div>

      {created && <NewKeyReveal created={created} onClose={() => setCreated(null)} />}

      {showCreate && (
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-4 space-y-3" data-testid="create-api-key-form">
          <div className="grid md:grid-cols-2 gap-3">
            <label className="text-xs text-zinc-400 space-y-1"><span>Nombre</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: CRM Produccion" data-testid="api-key-name-input" className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/50" />
            </label>
            <label className="text-xs text-zinc-400 space-y-1"><span>Webhook URL (opcional)</span>
              <input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} placeholder="https://tu-servidor.com/webhooks/rcs" data-testid="api-key-webhook-input" className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/50" />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} data-testid="cancel-create-key-btn" className="text-xs px-3 py-2 rounded-lg text-zinc-400 hover:text-white">Cancelar</button>
            <button onClick={create} data-testid="submit-create-key-btn" className="text-xs px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium">Crear key</button>
          </div>
        </div>
      )}

      {loading ? <p className="text-xs text-zinc-500">Cargando…</p> : keys.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 text-sm border border-dashed border-white/10 rounded-xl" data-testid="api-keys-empty">
          <ShieldOff className="w-6 h-6 mx-auto mb-2 opacity-50" /> Aun no hay API keys. Crea la primera para empezar a integrar.
        </div>
      ) : (
        <div className="grid gap-3">{keys.map((k) => <KeyRow key={k.id} k={k} onChanged={load} />)}</div>
      )}
    </div>
  );
};
